/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

/** Shared dimensions so "Add image" / "Add file" match in section headers and forms. */
export const COMPACT_UPLOAD_BUTTON_HEIGHT = 32;
export const COMPACT_UPLOAD_BUTTON_MIN_WIDTH = 116;

export const COMPACT_UPLOAD_BUTTON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  boxSizing: 'border-box',
  minWidth: COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
  width: COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
  height: COMPACT_UPLOAD_BUTTON_HEIGHT,
  px: 1.5,
  py: 0,
  border: '1px dashed',
  borderColor: 'divider',
  bgcolor: 'transparent',
  flexShrink: 0,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    bgcolor: 'action.hover',
  },
} as const satisfies SxProps<Theme>;

export const COMPACT_UPLOAD_LABELS = {
  image: 'Add image',
  file: 'Add file',
  uploading: 'Uploading…',
} as const;

export interface CompactUploadButtonProps {
  /** Shown when not uploading; defaults to file label. */
  label?: string;
  uploading?: boolean;
  disabled?: boolean;
  onClick: () => void;
  sx?: SxProps<Theme>;
}

export const CompactUploadButton: React.FC<CompactUploadButtonProps> = ({
  label = COMPACT_UPLOAD_LABELS.file,
  uploading = false,
  disabled = false,
  onClick,
  sx,
}) => {
  const theme = useTheme();
  const isDisabled = disabled && !uploading;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.5,
        minWidth: COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
      }}
    >
      <Paper
        variant="outlined"
        component="button"
        type="button"
        disabled={isDisabled}
        aria-busy={uploading}
        onClick={() => {
          if (!isDisabled && !uploading) onClick();
        }}
        sx={[
          COMPACT_UPLOAD_BUTTON_SX,
          {
            cursor: isDisabled ? 'not-allowed' : uploading ? 'wait' : 'pointer',
            opacity: isDisabled ? 0.55 : 1,
            pointerEvents: uploading ? 'none' : 'auto',
          },
          uploading
            ? {
                borderStyle: 'solid',
                borderWidth: 2,
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                animation: 'compactUploadPulse 1.4s ease-in-out infinite',
                '@keyframes compactUploadPulse': {
                  '0%, 100%': {
                    boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.45)}`,
                  },
                  '50%': {
                    boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0)}`,
                  },
                },
              }
            : {
                '&:hover': {
                  bgcolor: isDisabled ? 'transparent' : 'action.hover',
                  borderColor: isDisabled ? 'divider' : 'primary.light',
                },
              },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {uploading ? (
          <CircularProgress size={18} thickness={5} color="primary" />
        ) : (
          <CloudUpload sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: uploading ? 700 : 500,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            color: uploading ? 'primary.main' : 'text.primary',
          }}
        >
          {uploading ? COMPACT_UPLOAD_LABELS.uploading : label}
        </Typography>
      </Paper>
      {uploading ? (
        <LinearProgress
          color="primary"
          sx={{
            height: 3,
            borderRadius: 1,
            width: '100%',
          }}
        />
      ) : null}
    </Box>
  );
};

export default CompactUploadButton;
